import { Building2, ChevronRight, Loader2 } from 'lucide-react';
import { Text } from '@/components/ui/Text';
import type { Persona, ResolvedMembership } from '@/lib/session/session.types';

const PERSONA_LABEL: Record<Persona, string> = {
  SA: 'Service Admin',
  AGENCY: 'Agency',
  CLIENT: 'Client',
  VENDOR: 'Vendor',
  WORKER: 'Worker',
};

function personaText(persona: Persona): string {
  return PERSONA_LABEL[persona] ?? persona;
}

function label(ctx: ResolvedMembership): string {
  return ctx.displayName?.trim() || `${personaText(ctx.persona)} · ${ctx.contextTenantId.slice(0, 8)}`;
}

export interface ContextPickerProps {
  contexts: ResolvedMembership[];
  onSelect: (contextTenantId: string) => void;
  /** A switch is in flight (the just-clicked row spins). */
  isSwitching?: boolean;
  /** The tenant id currently being activated (for the spinner placement). */
  pendingTenantId?: string | null;
}

/**
 * Full-screen context chooser (T17). Shown when the user has no active context
 * yet — on fresh login with several contexts, or after `ACL_CONTEXT_REQUIRED`.
 * Selecting a row calls `switchContext`; only once it resolves does the app
 * render (the {@link ContextGate} swaps this out for the routed `Outlet`).
 */
export function ContextPicker({ contexts, onSelect, isSwitching, pendingTenantId }: ContextPickerProps) {
  return (
    <div className="flex min-h-[70vh] w-full items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <Text variant="headline-sm" as="h1" className="mb-1">Choose a workspace</Text>
          <Text variant="body-md" color="muted">
            Select the organisation you want to act in. You can switch any time from the top bar.
          </Text>
        </div>

        {contexts.length === 0 ? (
          <div className="rounded-lg border border-border bg-surface p-8 text-center">
            <Building2 size={28} className="mx-auto mb-2 text-text-subtle opacity-40" aria-hidden />
            <Text variant="body-md" color="muted">
              You don't have access to any contexts yet. Contact your administrator.
            </Text>
          </div>
        ) : (
          <div className="space-y-2" role="list" aria-label="Available contexts">
            {contexts.map((ctx) => {
              const pending = isSwitching && pendingTenantId === ctx.contextTenantId;
              return (
                <button
                  key={`${ctx.contextTenantId}:${ctx.persona}`}
                  type="button"
                  role="listitem"
                  disabled={isSwitching}
                  onClick={() => onSelect(ctx.contextTenantId)}
                  className="flex w-full items-center gap-3 rounded-lg border border-border bg-surface-elevated p-4 text-left outline-none transition-colors hover:border-primary/30 hover:bg-surface-muted focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
                >
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Building2 size={18} aria-hidden />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <Text variant="body-md" weight="semibold" as="span" className="truncate">{label(ctx)}</Text>
                    <Text variant="label-sm" color="subtle" as="span">{personaText(ctx.persona)}</Text>
                  </span>
                  {pending ? (
                    <Loader2 size={18} className="shrink-0 animate-spin text-primary" aria-label="Switching" />
                  ) : (
                    <ChevronRight size={18} className="shrink-0 text-text-subtle" aria-hidden />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
